import type { ServiceInfoState } from "./product-service-info";
import type { ServiceBinding } from "./service-templates";
import { request } from "./client";

export type ProductStatus = "DRAFT" | "ON_SALE" | "OFF_SALE";

export interface ProductSummary {
  isHot: boolean;
  id: string;
  productNo: string;
  name: string;
  subtitle: string | null;
  status: ProductStatus;
  customizationEnabled: boolean;
  sortOrder: number;
  skuCount: number;
  activeSkuCount: number;
  mainImageUrl: string | null;
  startingTierPrice: number | null;
  availability: "AVAILABLE" | "SOLD_OUT";
  updatedAt: string;
}

export interface AttributeOption {
  id: string;
  optionCode: string;
  optionName: string;
  status: "ACTIVE" | "INACTIVE";
}

export interface AttributeDefinition {
  id: string;
  attributeCode: string;
  attributeName: string;
  valueType: "OPTION" | "TEXT" | "NUMBER" | "BOOLEAN" | "STRUCTURED";
  scope: "PRODUCT" | "SKU" | "BOTH";
  options: AttributeOption[];
}

export interface ProductAttributeConfig {
  id: string;
  attributeDefinitionId: string;
  attributeCode: string;
  attributeName: string;
  valueType: AttributeDefinition["valueType"];
  isRequired: boolean;
  isVariantAttribute: boolean;
  sortOrder: number;
}

export interface SkuAttributeValue {
  attributeDefinitionId: string;
  attributeCode: string;
  attributeName: string;
  optionId: string | null;
  displayValue: string;
  isVariantAttribute: boolean;
}

export interface SkuView {
  id: string;
  skuNo: string;
  specSignature: string;
  specSummary: Record<string, string>;
  status: "ACTIVE" | "INACTIVE";
  retailEnabled: boolean;
  allowBreakPackage: boolean;
  sortOrder: number;
  attributeValues: SkuAttributeValue[];
  dimensions: { lengthMm: number | null; widthMm: number | null; heightMm: number | null; note: string | null };
  updatedAt: string;
  images: ProductImage[];
  resolvedImages: ProductImage[];
  packageUnits: PackageUnit[];
  quantityTiers: QuantityTier[];
  availability: "AVAILABLE" | "SOLD_OUT";
  inventoryInitialized: boolean;
}

export interface ProductImage {
  id: string;
  skuId: string | null;
  type: "MAIN" | "DETAIL" | "DIMENSION" | "ACTUAL";
  url: string;
  fileId: string | null;
  altText: string | null;
  sortOrder: number;
  status: "ACTIVE" | "INACTIVE";
  source: "PRODUCT" | "SKU";
  updatedAt: string;
}

export type ProductDetailSectionCode = "INTRODUCTION" | "SHIPPING_INFO" | "FILE_SPEC" | "CUSTOM_SERVICE";
export type ProductDetailBlockType = "TEXT" | "IMAGE" | "IMAGE_TEXT";

export interface ProductDetailBlock {
  id: string;
  type: ProductDetailBlockType;
  title: string | null;
  body: string | null;
  sortOrder: number;
  status?: "ACTIVE" | "INACTIVE";
  updatedAt?: string;
  image: { id: string; url: string; altText: string | null } | null;
}

export interface ProductDetailSection {
  id: string;
  code: ProductDetailSectionCode;
  title: string;
  sortOrder: number;
  status?: "ACTIVE" | "INACTIVE";
  updatedAt?: string;
  blocks: ProductDetailBlock[];
}

export type CustomOptionInputType = "SINGLE_SELECT" | "MULTI_SELECT" | "NUMBER" | "TEXT";

export interface CustomOptionValue {
  id: string;
  groupId: string;
  code: string;
  name: string;
  extraConfig: unknown;
  imageUrl: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomOptionPresetValue {
  id: string;
  presetId: string;
  code: string;
  name: string;
  imageUrl: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomOptionPreset {
  id: string;
  code: string;
  name: string;
  inputType: CustomOptionInputType;
  required: boolean;
  unit: string | null;
  config: Record<string, unknown> | null;
  sortOrder: number;
  isActive: boolean;
  values: CustomOptionPresetValue[];
  createdAt: string;
  updatedAt: string;
}

export interface CustomOptionGroup {
  id: string;
  productId: string;
  code: string;
  name: string;
  inputType: CustomOptionInputType;
  required: boolean;
  unit: string | null;
  config: unknown;
  sortOrder: number;
  isActive: boolean;
  values: CustomOptionValue[];
  createdAt: string;
  updatedAt: string;
}

export interface AdminCustomizationConfig {
  product: {
    id: string;
    productNo: string;
    name: string;
    subtitle: string | null;
    categoryId: string | null;
    detailContent: string | null;
    customizationEnabled: boolean;
    status: ProductStatus;
    shippingLeadTime: string | null;
    shippingDescription: string | null;
    freeShippingDescription: string | null;
    guaranteeDescription: string | null;
    craftsmanshipDescription: string | null;
    invoiceDescription: string | null;
  };
  images: ProductImage[];
  optionGroups: CustomOptionGroup[];
  optionValues: CustomOptionValue[];
  referencePriceTiers: unknown[];
  detailSections: ProductDetailSection[];
}

export interface PackageUnit {
  id: string;
  unitType: "PIECE" | "PACK" | "BOX";
  unitName: string;
  parentUnitId: string | null;
  parentUnitCount: number | null;
  basePieceCount: number;
  isSalesUnit: boolean;
  status: "ACTIVE" | "INACTIVE";
  conversionText: string;
}

export interface QuantityTier {
  id: string;
  salesPackageUnitId: string;
  tierPieceQty: number;
  packageQty: number;
  retailTierPrice: number;
  status: "ACTIVE" | "INACTIVE";
  sortOrder: number;
  label: string;
  packageLabel: string;
  updatedAt: string;
}

export interface PublishCheck {
  passed: boolean;
  groups: Array<{
    group: string;
    status: "PASS" | "FAIL" | "UNAVAILABLE";
    items: Array<{ code: string; message: string; objectId?: string }>;
  }>;
}

export interface ProductDetail extends ProductSummary {
  serviceInfo?: ServiceInfoState;
  serviceTemplate?: ServiceBinding | null;
  material: string | null;
  categoryId: string | null;
  detailContent: string | null;
  shippingLeadTime: string | null;
  shippingDescription: string | null;
  freeShippingDescription: string | null;
  guaranteeDescription: string | null;
  craftsmanshipDescription: string | null;
  invoiceDescription: string | null;
  customOrderEnabled: boolean;
  customizationMoq: number | null;
  freightTemplateId: string | null;
  attributeConfigs: ProductAttributeConfig[];
  images: ProductImage[];
  skus: SkuView[];
  detailSections: ProductDetailSection[];
}

export interface ProductCategory {
  id: string;
  code: string;
  name: string;
  parentId: string | null;
  scope: "STOCK" | "CUSTOM" | "BOTH";
  sortOrder: number;
}

export interface FreightTemplate {
  id: string;
  templateNo: string;
  name: string;
  ruleType: "FREE" | "FIXED" | "CONDITIONAL_FREE";
  fixedAmount: number;
  freeThresholdAmount: number | null;
  status: "ACTIVE" | "INACTIVE";
}

export async function listProducts(): Promise<ProductSummary[]> {
  return (await request<{ items: ProductSummary[] }>("/admin/products?page=1&pageSize=100")).data.items;
}

export async function getProduct(productId: string): Promise<ProductDetail> {
  return (await request<ProductDetail>(`/admin/products/${productId}`)).data;
}

export async function getAdminCustomizationConfig(productId: string): Promise<AdminCustomizationConfig> {
  return (await request<AdminCustomizationConfig>(`/admin/products/${productId}/customization-config`)).data;
}

export async function listCustomOptionPresets(): Promise<CustomOptionPreset[]> {
  return (await request<CustomOptionPreset[]>("/admin/products/custom-option-presets")).data;
}

export async function createCustomOptionPreset(input: {
  code?: string;
  name: string;
  inputType: CustomOptionInputType;
  isRequired: boolean;
  unit: string | null;
  config: Record<string, unknown> | null;
  sortOrder: number;
}): Promise<CustomOptionPreset> {
  return (await request<CustomOptionPreset>("/admin/products/custom-option-presets", {
    method: "POST",
    body: JSON.stringify(input),
  })).data;
}

export async function updateCustomOptionPreset(
  preset: CustomOptionPreset,
  input: {
    expectedUpdatedAt?: string;
    code?: string;
    name?: string;
    inputType?: CustomOptionInputType;
    isRequired?: boolean;
    unit?: string | null;
    config?: Record<string, unknown> | null;
    sortOrder?: number;
  },
): Promise<CustomOptionPreset> {
  return (await request<CustomOptionPreset>(`/admin/products/custom-option-presets/${preset.id}`, {
    method: "PATCH",
    body: JSON.stringify({ ...input, expectedUpdatedAt: input.expectedUpdatedAt ?? preset.updatedAt }),
  })).data;
}

export async function setCustomOptionPresetStatus(
  preset: CustomOptionPreset,
  status: "ACTIVE" | "INACTIVE",
): Promise<CustomOptionPreset> {
  return (await request<CustomOptionPreset>(
    `/admin/products/custom-option-presets/${preset.id}/${status === "ACTIVE" ? "activate" : "deactivate"}`,
    { method: "POST", body: JSON.stringify({}) },
  )).data;
}

export async function createCustomOptionPresetValue(
  preset: CustomOptionPreset,
  input: { code?: string; name: string; imageUrl?: string | null; sortOrder: number },
): Promise<CustomOptionPreset> {
  return (await request<CustomOptionPreset>(`/admin/products/custom-option-presets/${preset.id}/values`, {
    method: "POST",
    body: JSON.stringify(input),
  })).data;
}

export async function updateCustomOptionPresetValue(
  preset: CustomOptionPreset,
  value: CustomOptionPresetValue,
  input: { code?: string; name?: string; imageUrl?: string | null; sortOrder?: number },
): Promise<CustomOptionPreset> {
  return (await request<CustomOptionPreset>(`/admin/products/custom-option-presets/${preset.id}/values/${value.id}`, {
    method: "PATCH",
    body: JSON.stringify({ ...input, expectedUpdatedAt: value.updatedAt }),
  })).data;
}

export async function setCustomOptionPresetValueStatus(
  preset: CustomOptionPreset,
  value: CustomOptionPresetValue,
  status: "ACTIVE" | "INACTIVE",
): Promise<CustomOptionPreset> {
  return (await request<CustomOptionPreset>(
    `/admin/products/custom-option-presets/${preset.id}/values/${value.id}/${status === "ACTIVE" ? "activate" : "deactivate"}`,
    { method: "POST", body: JSON.stringify({}) },
  )).data;
}

export async function applyCustomOptionPresets(
  productId: string, presetIds: string[], expectedMaterialGroup?: { id: string; expectedUpdatedAt: string } | null,
  copyStickerMaterials?: true,
): Promise<AdminCustomizationConfig> {
  return (await request<AdminCustomizationConfig>(`/admin/products/${productId}/customization-option-presets/apply`, {
    method: "POST",
    body: JSON.stringify({ presetIds, expectedMaterialGroup, copyStickerMaterials }),
  })).data;
}

export async function createCustomOptionGroup(
  productId: string,
  input: {
    code?: string;
    name: string;
    inputType: CustomOptionInputType;
    isRequired: boolean;
    unit: string | null;
    config: Record<string, unknown> | null;
    sortOrder: number;
  },
): Promise<CustomOptionGroup> {
  return (await request<CustomOptionGroup>(`/admin/products/${productId}/customization-option-groups`, {
    method: "POST",
    body: JSON.stringify(input),
  })).data;
}

export async function updateCustomOptionGroup(
  productId: string,
  groupId: string,
  input: {
    expectedUpdatedAt: string;
    code?: string;
    name?: string;
    inputType?: CustomOptionInputType;
    isRequired?: boolean;
    unit?: string | null;
    config?: Record<string, unknown> | null;
    sortOrder?: number;
  },
): Promise<CustomOptionGroup> {
  return (await request<CustomOptionGroup>(
    `/admin/products/${productId}/customization-option-groups/${groupId}`,
    { method: "PATCH", body: JSON.stringify(input) },
  )).data;
}

export async function setCustomOptionGroupStatus(
  productId: string,
  groupId: string,
  status: "ACTIVE" | "INACTIVE",
): Promise<CustomOptionGroup> {
  return (await request<CustomOptionGroup>(
    `/admin/products/${productId}/customization-option-groups/${groupId}/${status === "ACTIVE" ? "activate" : "deactivate"}`,
    { method: "POST", body: JSON.stringify({}) },
  )).data;
}

export async function createCustomOptionValue(
  productId: string,
  groupId: string,
  input: { code?: string; name: string; extraConfig: Record<string, unknown> | null; imageUrl?: string | null; sortOrder: number },
): Promise<CustomOptionValue> {
  return (await request<CustomOptionValue>(
    `/admin/products/${productId}/customization-option-groups/${groupId}/values`,
    { method: "POST", body: JSON.stringify(input) },
  )).data;
}

export async function updateCustomOptionValue(
  productId: string,
  groupId: string,
  valueId: string,
  input: {
    expectedUpdatedAt: string;
    code?: string;
    name?: string;
    extraConfig?: Record<string, unknown> | null;
    imageUrl?: string | null;
    sortOrder?: number;
  },
): Promise<CustomOptionValue> {
  return (await request<CustomOptionValue>(
    `/admin/products/${productId}/customization-option-groups/${groupId}/values/${valueId}`,
    { method: "PATCH", body: JSON.stringify(input) },
  )).data;
}

export async function setCustomOptionValueStatus(
  productId: string,
  groupId: string,
  valueId: string,
  status: "ACTIVE" | "INACTIVE",
): Promise<CustomOptionValue> {
  return (await request<CustomOptionValue>(
    `/admin/products/${productId}/customization-option-groups/${groupId}/values/${valueId}/${status === "ACTIVE" ? "activate" : "deactivate"}`,
    { method: "POST", body: JSON.stringify({}) },
  )).data;
}

export async function listProductCategories(): Promise<ProductCategory[]> {
  return (await request<ProductCategory[]>("/admin/products/categories")).data;
}

export async function listFreightTemplates(): Promise<FreightTemplate[]> {
  return (await request<FreightTemplate[]>("/admin/freight-templates")).data;
}

export async function createProduct(input: {
  isHot?: boolean;
  productNo: string;
  name: string;
  subtitle?: string;
  categoryId?: string | null;
  detailContent?: string;
  freightTemplateId?: string | null;
    customizationEnabled: boolean;
    shippingLeadTime?: string | null;
    shippingDescription?: string | null;
    freeShippingDescription?: string | null;
    guaranteeDescription?: string | null;
    craftsmanshipDescription?: string | null;
    invoiceDescription?: string | null;
    customOrderEnabled?: boolean;
  customizationMoq?: number | null;
  sortOrder: number;
}): Promise<ProductDetail> {
  return (
    await request<ProductDetail>("/admin/products", {
      method: "POST",
      body: JSON.stringify({
        ...input,
      }),
    })
  ).data;
}

export type ProductUpdateInput = Partial<Pick<ProductDetail,
  "name" | "subtitle" | "categoryId" | "detailContent" | "customizationEnabled" | "freightTemplateId" | "sortOrder" | "isHot"
>>;

export function productUpdateChanges(product: ProductDetail, input: ProductUpdateInput): ProductUpdateInput {
  // A popular-only edit must not unlock structural changes to an ON_SALE product.
  if (input.isHot !== undefined && (Object.keys(input) as Array<keyof ProductUpdateInput>)
    .every((key) => key === "isHot" || input[key] === product[key])) {
    return { isHot: input.isHot };
  }
  return input;
}

export async function updateProduct(product: ProductDetail, input: ProductUpdateInput): Promise<ProductDetail> {
  return (await request<ProductDetail>(`/admin/products/${product.id}`, {
    method: "PATCH",
    body: JSON.stringify({ ...productUpdateChanges(product, input), expectedUpdatedAt: product.updatedAt }),
  })).data;
}

export async function updateProductMaterial(productId: string, material: string): Promise<ProductDetail> {
  return (await request<ProductDetail>(`/admin/products/${productId}/material`, {
    method: "PUT",
    body: JSON.stringify({ material }),
  })).data;
}

export async function updateProductParameters(
  product: ProductDetail,
  input: {
    shippingLeadTime: string | null;
    shippingDescription: string | null;
    freeShippingDescription: string | null;
    guaranteeDescription: string | null;
    craftsmanshipDescription: string | null;
    invoiceDescription: string | null;
    customOrderEnabled: boolean;
    customizationMoq: number | null;
  },
): Promise<ProductDetail> {
  return (await request<ProductDetail>(`/admin/products/${product.id}/parameters`, {
    method: "PATCH",
    body: JSON.stringify({ ...input, expectedUpdatedAt: product.updatedAt }),
  })).data;
}

export async function unpublishProduct(productId: string, reason: string): Promise<void> {
  await request(`/admin/products/${productId}/unpublish`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function archiveProduct(productId: string, reason: string): Promise<void> {
  await request(`/admin/products/${productId}/archive`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function listAttributes(): Promise<AttributeDefinition[]> {
  return (await request<AttributeDefinition[]>("/admin/product-attributes")).data;
}

export async function createOptionAttribute(input: {
  attributeCode: string;
  attributeName: string;
  optionCode: string;
  optionName: string;
}): Promise<{ attributeId: string; optionId: string }> {
  const attribute = await request<{ id: string }>("/admin/product-attributes", {
    method: "POST",
    body: JSON.stringify({
      attributeCode: input.attributeCode,
      attributeName: input.attributeName,
      valueType: "OPTION",
      scope: "SKU",
      defaultIsVariantAttribute: true,
      sortOrder: 0,
    }),
  });
  const option = await request<{ id: string }>(
    `/admin/product-attributes/${attribute.data.id}/options`,
    {
      method: "POST",
      body: JSON.stringify({
        optionCode: input.optionCode,
        optionName: input.optionName,
        sortOrder: 0,
      }),
    },
  );
  return { attributeId: attribute.data.id, optionId: option.data.id };
}

export async function configureSingleVariantAttribute(
  productId: string,
  attributeDefinitionId: string,
): Promise<void> {
  await request(`/admin/products/${productId}/attribute-configs`, {
    method: "PUT",
    body: JSON.stringify({
      configs: [
        {
          attributeDefinitionId,
          isRequired: true,
          isVariantAttribute: true,
          sortOrder: 0,
        },
      ],
    }),
  });
}

export async function createSku(
  productId: string,
  input: { attributeDefinitionId?: string; optionId?: string; displayValue?: string },
): Promise<SkuView> {
  const attributeValues =
    input.attributeDefinitionId && input.optionId && input.displayValue
      ? [
          {
            attributeDefinitionId: input.attributeDefinitionId,
            optionId: input.optionId,
            displayValue: input.displayValue,
          },
        ]
      : [];
  return (
    await request<SkuView>(`/admin/products/${productId}/skus`, {
      method: "POST",
      body: JSON.stringify({
        attributeValues,
        dimensions: {},
        allowBreakPackage: false,
        retailEnabled: true,
        sortOrder: 0,
      }),
    })
  ).data;
}

export async function createInlineSku(
  productId: string,
  input: {
    specName: string;
    tierPieceQty: number;
    retailTierPrice: number;
    salesUnitName: string;
    basePieceCount: number;
  },
): Promise<SkuView> {
  return (
    await request<SkuView>(`/admin/products/${productId}/specs`, {
      method: "POST",
      body: JSON.stringify(input),
    })
  ).data;
}

export async function activateSku(productId: string, skuId: string): Promise<SkuView> {
  return (
    await request<SkuView>(`/admin/products/${productId}/skus/${skuId}/activate`, {
      method: "POST",
      body: JSON.stringify({}),
    })
  ).data;
}

export async function updateSku(productId: string, sku: SkuView, input: {
  dimensionNote?: string | null;
  allowBreakPackage?: boolean;
  retailEnabled?: boolean;
  sortOrder?: number;
  attributeValues?: Array<{
    attributeDefinitionId: string;
    optionId?: string | null;
    displayValue: string;
  }>;
}): Promise<SkuView> {
  return (await request<SkuView>(`/admin/products/${productId}/skus/${sku.id}`, {
    method: "PATCH",
    body: JSON.stringify({ ...input, expectedUpdatedAt: sku.updatedAt }),
  })).data;
}

export async function deactivateSku(productId: string, skuId: string): Promise<SkuView> {
  return (await request<SkuView>(`/admin/products/${productId}/skus/${skuId}/deactivate`, {
    method: "POST",
    body: JSON.stringify({}),
  })).data;
}

export async function createImage(
  productId: string,
  input: { skuId?: string | null; type: ProductImage["type"]; url: string; fileId?: string | null; altText?: string; sortOrder: number },
): Promise<ProductImage> {
  return (
    await request<ProductImage>(`/admin/products/${productId}/images`, {
      method: "POST",
      body: JSON.stringify(input),
    })
  ).data;
}

export async function uploadProductImage(file: File): Promise<{ fileId: string; url: string }> {
  const contentBase64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",", 2)[1] ?? "");
    reader.onerror = () => reject(reader.error ?? new Error("读取图片失败"));
    reader.readAsDataURL(file);
  });
  return (await request<{ fileId: string; url: string }>("/admin/uploads/product-images", { method: "POST", body: JSON.stringify({ fileName: file.name, contentType: file.type, contentBase64 }) })).data;
}

export async function uploadCustomizationOptionImage(file: File): Promise<{ fileId: string; url: string }> {
  const contentBase64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",", 2)[1] ?? "");
    reader.onerror = () => reject(reader.error ?? new Error("读取图片失败"));
    reader.readAsDataURL(file);
  });
  return (await request<{ fileId: string; url: string }>("/admin/uploads/customization-option-images", {
    method: "POST",
    body: JSON.stringify({ fileName: file.name, contentType: file.type, contentBase64 }),
  })).data;
}

export async function deactivateImage(productId: string, imageId: string): Promise<void> {
  await request(`/admin/products/${productId}/images/${imageId}/deactivate`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function activateImage(productId: string, imageId: string): Promise<void> {
  await request(`/admin/products/${productId}/images/${imageId}/activate`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function deleteImage(productId: string, imageId: string): Promise<void> {
  await request(`/admin/products/${productId}/images/${imageId}`, { method: "DELETE" });
}

export async function reorderProductGallery(productId: string, imageIds: string[]): Promise<ProductImage[]> {
  return (await request<ProductImage[]>(`/admin/products/${productId}/images/reorder`, {
    method: "POST",
    body: JSON.stringify({ imageIds }),
  })).data;
}

export async function updateImage(productId: string, image: ProductImage, input: {
  url?: string | null;
  fileId?: string | null;
  thumbnailUrl?: string | null;
  altText?: string | null;
  sortOrder?: number;
  expectedUpdatedAt?: string;
}): Promise<ProductImage> {
  return (await request<ProductImage>(`/admin/products/${productId}/images/${image.id}`, {
    method: "PATCH",
    body: JSON.stringify({ ...input, expectedUpdatedAt: input.expectedUpdatedAt ?? image.updatedAt }),
  })).data;
}

export async function createDetailSection(
  productId: string,
  input: { code: ProductDetailSectionCode; title: string; sortOrder: number },
): Promise<{ id: string }> {
  return (await request<{ id: string }>(`/admin/products/${productId}/detail-sections`, {
    method: "POST",
    body: JSON.stringify(input),
  })).data;
}

export async function updateDetailSection(
  productId: string,
  sectionId: string,
  input: { title?: string; sortOrder?: number; expectedUpdatedAt: string },
): Promise<ProductDetailSection> {
  return (await request<ProductDetailSection>(`/admin/products/${productId}/detail-sections/${sectionId}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  })).data;
}

export async function deactivateDetailSection(productId: string, sectionId: string): Promise<void> {
  await request(`/admin/products/${productId}/detail-sections/${sectionId}/deactivate`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function createDetailBlock(
  productId: string,
  sectionId: string,
  input: {
    type: ProductDetailBlockType;
    title?: string | null;
    body?: string | null;
    productImageId?: string | null;
    sortOrder: number;
  },
): Promise<{ id: string }> {
  return (await request<{ id: string }>(`/admin/products/${productId}/detail-sections/${sectionId}/blocks`, {
    method: "POST",
    body: JSON.stringify(input),
  })).data;
}

export async function updateDetailBlock(
  productId: string,
  blockId: string,
  input: {
    type?: ProductDetailBlockType;
    title?: string | null;
    body?: string | null;
    productImageId?: string | null;
    sortOrder?: number;
    expectedUpdatedAt: string;
  },
): Promise<ProductDetailBlock> {
  return (await request<ProductDetailBlock>(`/admin/products/${productId}/detail-blocks/${blockId}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  })).data;
}

export async function deactivateDetailBlock(productId: string, blockId: string): Promise<void> {
  await request(`/admin/products/${productId}/detail-blocks/${blockId}/deactivate`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function createPackageUnit(
  productId: string,
  skuId: string,
  input: {
    unitType: PackageUnit["unitType"];
    unitName: string;
    parentUnitId?: string | null;
    parentUnitCount?: number | null;
    isSalesUnit: boolean;
  },
): Promise<PackageUnit> {
  return (
    await request<PackageUnit>(`/admin/products/${productId}/skus/${skuId}/package-units`, {
      method: "POST",
      body: JSON.stringify(input),
    })
  ).data;
}

export async function deactivatePackageUnit(
  productId: string,
  skuId: string,
  packageUnitId: string,
): Promise<void> {
  await request(
    `/admin/products/${productId}/skus/${skuId}/package-units/${packageUnitId}/deactivate`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export async function updatePackageUnitName(
  productId: string,
  skuId: string,
  packageUnitId: string,
  input: { unitName: string },
): Promise<PackageUnit> {
  return (
    await request<PackageUnit>(
      `/admin/products/${productId}/skus/${skuId}/package-units/${packageUnitId}`,
      { method: "PATCH", body: JSON.stringify(input) },
    )
  ).data;
}

export async function createQuantityTier(
  productId: string,
  skuId: string,
  input: { salesPackageUnitId: string; tierPieceQty: number; retailTierPrice: number; sortOrder: number },
): Promise<QuantityTier> {
  return (
    await request<QuantityTier>(`/admin/products/${productId}/skus/${skuId}/quantity-tiers`, {
      method: "POST",
      body: JSON.stringify(input),
    })
  ).data;
}

export async function deactivateQuantityTier(
  productId: string,
  skuId: string,
  quantityTierId: string,
): Promise<void> {
  await request(
    `/admin/products/${productId}/skus/${skuId}/quantity-tiers/${quantityTierId}/deactivate`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export async function updateQuantityTier(
  productId: string,
  skuId: string,
  tier: QuantityTier,
  input: { tierPieceQty?: number; retailTierPrice?: number; sortOrder?: number },
): Promise<QuantityTier> {
  return (await request<QuantityTier>(
    `/admin/products/${productId}/skus/${skuId}/quantity-tiers/${tier.id}`,
    { method: "PATCH", body: JSON.stringify({ ...input, expectedUpdatedAt: tier.updatedAt }) },
  )).data;
}

export async function checkPublish(productId: string): Promise<PublishCheck> {
  return (
    await request<PublishCheck>(`/admin/products/${productId}/publish-check`, {
      method: "POST",
      body: JSON.stringify({}),
    })
  ).data;
}

export async function publishProduct(productId: string): Promise<void> {
  await request(`/admin/products/${productId}/publish`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}
